import { useEffect, useMemo, useState } from "react";
import WebApp from "@twa-dev/sdk";
import {
  CounterLpVault,
  enrichAxmPools,
  fetchIndexerPools,
  filterMarketsByMinTvlUsd,
  pinMarketsFirst,
  shortAddress,
  type EnrichedMarket,
} from "@axiome/counter-lp-sdk";
import { useAxiomeWallet } from "./hooks/useAxiomeWallet";
import { ConnectModal } from "./components/ConnectModal";
import { AdminPanel } from "./components/AdminPanel";
import {
  HAS_LIVE_VAULT,
  INDEXER_URL,
  REST_URL,
  VAULT_ADDRESS,
} from "./lib/config";

export function AdminApp() {
  const wallet = useAxiomeWallet();
  const [ready, setReady] = useState(false);
  const [vaultAdmin, setVaultAdmin] = useState("");
  const [indexerMarkets, setIndexerMarkets] = useState<EnrichedMarket[]>([]);
  const [vaultMarkets, setVaultMarkets] = useState<
    Array<{ symbol: string; pool: string }>
  >([]);
  const [status, setStatus] = useState<string | null>(null);

  const vault = useMemo(
    () =>
      new CounterLpVault({
        vaultAddress: VAULT_ADDRESS,
        restUrl: REST_URL,
      }),
    [],
  );

  const isAdmin =
    !!wallet.account && !!vaultAdmin && wallet.account === vaultAdmin;

  const registerCandidates = useMemo(() => {
    const registered = new Set(vaultMarkets.map((m) => m.pool.toLowerCase()));
    return indexerMarkets.filter((m) => !registered.has(m.pool.toLowerCase()));
  }, [indexerMarkets, vaultMarkets]);

  useEffect(() => {
    try {
      WebApp.ready();
      WebApp.expand();
      WebApp.setHeaderColor("#000000");
      WebApp.setBackgroundColor("#000000");
    } catch {
      /* browser */
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    async function load() {
      if (!HAS_LIVE_VAULT) return;
      try {
        const [c, m, listed] = await Promise.all([
          vault.config(),
          vault.markets(),
          fetchIndexerPools({ perPage: 100, indexer: INDEXER_URL })
            .then((raw) => pinMarketsFirst(filterMarketsByMinTvlUsd(enrichAxmPools(raw), 100)))
            .catch(() => [] as EnrichedMarket[]),
        ]);
        if (!alive) return;
        setReady(true);
        setVaultAdmin(c.admin || "");
        setVaultMarkets((m || []).map((x) => ({ symbol: x.symbol, pool: x.pool })));
        setIndexerMarkets(listed);
      } catch {
        if (!alive) return;
        setReady(false);
        retryTimer = setTimeout(() => {
          if (alive) void load();
        }, 8_000);
      }
    }

    void load();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [vault]);

  return (
    <div className="app-shell">
      <div className="wallet-bar">
        <div className="wallet-chip">
          {wallet.account
            ? `${shortAddress(wallet.account)}${isAdmin ? " · администратор" : ""}`
            : "Подключите кошелёк"}
        </div>
        {wallet.account ? (
          <button className="btn btn-ghost btn-sm" onClick={wallet.disconnect}>
            Выйти
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={wallet.startConnect}>
            Войти
          </button>
        )}
      </div>

      <h1 className="brand">
        <span>Counter LP</span>
        <em>Панель управления</em>
      </h1>
      <p className="hero-copy">
        Войдите кошельком администратора через Axiome Connect, чтобы управлять рынками и
        казной.
      </p>

      {!ready && wallet.account ? (
        <p className="hint" style={{ textAlign: "center" }}>
          Подключаемся к сети…
        </p>
      ) : null}

      {!wallet.account ? (
        <div className="section">
          <div className="cta-row">
            <button className="btn btn-primary" onClick={wallet.startConnect}>
              Подключить кошелёк
            </button>
          </div>
        </div>
      ) : !isAdmin ? (
        <div className="section">
          <p className="lead">
            Этот кошелёк не является администратором. Выйдите и подключите нужный адрес.
          </p>
          <div className="cta-row">
            <button className="btn btn-ghost" onClick={wallet.disconnect}>
              Сменить кошелёк
            </button>
          </div>
        </div>
      ) : (
        <AdminPanel
          markets={registerCandidates}
          registeredCount={vaultMarkets.length}
          account={wallet.account}
          isAdmin={isAdmin && ready}
          onSign={async (msgs) => {
            if (!ready) throw new Error("Сеть обновляется — попробуйте через минуту");
            await wallet.sign(msgs);
          }}
        />
      )}

      {status ? <p className="status">{status}</p> : null}
      {wallet.error ? <p className="status err">{wallet.error}</p> : null}

      {wallet.connecting && wallet.authCode && wallet.qrPayload ? (
        <ConnectModal
          code={wallet.authCode}
          qrPayload={wallet.qrPayload}
          onClose={wallet.cancelConnect}
        />
      ) : null}
    </div>
  );
}
